import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Zap, Target, TrendingUp, BarChart2, Clock, Award, Star } from 'lucide-react';
import { AppState } from '../../types';
import { GroupId } from '../../config/systemConfig';
import { MODULES } from '../../data/modules';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { SkillMasteryPanel } from '../dashboard/SkillMasteryPanel';
import { StatsDrawer } from '../dashboard/StatsDrawer';
interface ProgressPageProps {
  state: AppState;
  selectedGroup: GroupId;
}
export function ProgressPage({
  state,
  selectedGroup
}: ProgressPageProps) {
  // Calculate metrics
  const totalQuestionsAnswered = Object.values(state.progress).reduce((sum, mod) => sum + mod.questionsAnswered.length, 0);
  const averageMastery = Math.round(Object.values(state.progress).reduce((sum, mod) => sum + mod.masteryPercentage, 0) / MODULES.length);
  const totalMasteredModules = Object.values(state.progress).filter(mod => mod.masteryPercentage >= 95).length;
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24
      }
    }
  };
  return <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-8 pb-12" data-id="element-1548">
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between" data-id="element-1549">
        <div data-id="element-1550">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-1551">Jouw Voortgang</h1>
          <p className="text-slate-500 mt-1 text-lg" data-id="element-1552">
            Bekijk hoe ver je bent gekomen in groep {selectedGroup}
          </p>
        </div>
        <div className="hidden md:block" data-id="element-1553">
          <Badge variant="info" size="lg" icon={<Clock className="w-4 h-4" data-id="element-1555" />} data-id="element-1554">
            Schooljaar 2025-2026
          </Badge>
        </div>
      </motion.div>

      {/* Key Metrics Grid */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1556">
        <StatCard icon={<Zap className="w-6 h-6 text-amber-600 fill-amber-600" data-id="element-1558" />} label="Totale XP" value={state.stats.xp.toLocaleString()} subValue="Punten verdiend" color="bg-amber-50 border-amber-200/60" iconBg="bg-amber-100" data-id="element-1557" />
        <StatCard icon={<Star className="w-6 h-6 text-indigo-600 fill-indigo-600" data-id="element-1560" />} label="Huidig Level" value={state.stats.level.toString()} subValue="Blijf groeien!" color="bg-indigo-50 border-indigo-200/60" iconBg="bg-indigo-100" data-id="element-1559" />
        <StatCard icon={<Target className="w-6 h-6 text-emerald-600" data-id="element-1562" />} label="Vragen Gemaakt" value={totalQuestionsAnswered.toString()} subValue="Oefenopgaven" color="bg-emerald-50 border-emerald-200/60" iconBg="bg-emerald-100" data-id="element-1561" />
        <StatCard icon={<TrendingUp className="w-6 h-6 text-blue-600" data-id="element-1564" />} label="Gem. Beheersing" value={`${averageMastery}%`} subValue={`${totalMasteredModules} vakken beheerst`} color="bg-blue-50 border-blue-200/60" iconBg="bg-blue-100" data-id="element-1563" />
      </motion.div>

      {/* Main Content Sections */}
      <motion.div variants={itemVariants} data-id="element-1565">
        <SkillMasteryPanel data-id="element-1566" />
      </motion.div>

      <motion.div variants={itemVariants} data-id="element-1567">
        <StatsDrawer state={state} selectedGroup={selectedGroup} data-id="element-1568" />
      </motion.div>

      {/* Recent Activity / Encouragement */}
      <motion.div variants={itemVariants} data-id="element-1569">
        <Card className="p-6 bg-gradient-to-br from-indigo-500 to-purple-600 text-white border-none shadow-lg" data-id="element-1570">
          <div className="flex items-start gap-4" data-id="element-1571">
            <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm" data-id="element-1572">
              <Award className="w-8 h-8 text-white" data-id="element-1573" />
            </div>
            <div data-id="element-1574">
              <h3 className="text-xl font-bold mb-2" data-id="element-1575">
                Goed bezig, {state.stats.name}!
              </h3>
              <p className="text-indigo-100 max-w-2xl leading-relaxed" data-id="element-1576">
                Je hebt al {totalQuestionsAnswered} vragen beantwoord. Door elke
                dag even te oefenen word je steeds beter. Ga zo door met het
                verzamelen van XP en het beheersen van nieuwe vaardigheden!
              </p>
            </div>
          </div>
        </Card>
      </motion.div>
    </motion.div>;
}
function StatCard({
  icon,
  label,
  value,
  subValue,
  color,
  iconBg
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  subValue: string;
  color: string;
  iconBg: string;
}) {
  return <Card className={`p-5 border ${color}`} data-id="element-1577">
      <div className="flex items-start justify-between mb-4" data-id="element-1578">
        <div className={`p-3 rounded-xl ${iconBg}`} data-id="element-1579">{icon}</div>
        <div className="text-right" data-id="element-1580">
          <div className="text-3xl font-bold text-slate-900 tabular-nums tracking-tight" data-id="element-1581">
            {value}
          </div>
        </div>
      </div>
      <div data-id="element-1582">
        <div className="font-bold text-slate-900" data-id="element-1583">{label}</div>
        <div className="text-xs font-medium text-slate-500 mt-0.5" data-id="element-1584">
          {subValue}
        </div>
      </div>
    </Card>;
}